import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';

import {
  CACHE_CONTENT_ACTIONS,
  CACHE_STORAGE_APP_CACHE_NAME,
  CACHE_STORAGE_BOOK_CACHE_PREFIX,
  ACCESS_OFFLINE_CONTENT_DIALOG_TIMEOUT,
  OFFLINE_CHAPTER_STATUS,
  STATUS_CODES,
} from 'constants';
import {
  BookValuesContext,
  MobileAppValuesContext,
  NetworkStatusValuesContext,
  OfflineModifiersContext,
  OfflineValuesContext,
  UserContentValuesContext,
  UserSessionModifiersContext,
  UserSessionValuesContext,
} from 'contexts';
import { useBookContentBasePath, useStaticResourcePaths } from 'hooks';
import {
  areSetsEqual,
  getIndices,
  getLocalSettingsProxy,
  getMatchingMapEntryKeys,
  promiseBookReadyForOffline,
} from 'utilities';
import {
  cacheAllDocuments,
  getCacheHashMap,
  getCacheHashMapChanges,
  getDocumentsProgress,
  getDownloadedPathsFromCache,
  getMarkersProgress,
  getOverallProgress,
} from 'utilities/offlineUtilities';
import { UserContentOfflineSession } from 'types';

const progressDefaults = {
  overallProgress: new Map(),
  documentsProgress: new Map(),
  markersProgress: new Map(),
};
const offlineSessionDataDefaults = {
  absolutePaths: new Set(),
  absolutePathsByDocument: new Map(),
  markers: new Set(),
  documents: new Set(),
};

export const OfflineProvider = ({ children }) => {
  /**
   * Get Context Values and Modifiers
   */
  const client = useApolloClient();
  const promiseBookReadyForOfflineRef = useRef();
  const { book } = useContext(BookValuesContext);
  const { hasConnection, isOffline } = useContext(NetworkStatusValuesContext);
  const { isMobileApp } = useContext(MobileAppValuesContext);
  const { deviceUUID, isSessionExpired, shouldAlwaysDownloadDocuments } =
    useContext(UserSessionValuesContext);
  const { setIsSessionExpired } = useContext(UserSessionModifiersContext);
  const userContent = useContext(UserContentValuesContext);

  const offlineSession = userContent?.offlineSession;
  const offlineSessionDataReceived = useRef(
    Boolean(offlineSession !== undefined),
  ); // offlineSession is undefined if current userContent was created without data for offlineSession
  const isOfflineForReader = isMobileApp ? false : isOffline; // never offline for mobileApp so that requests to backend can be spoofed providing offline experience for mobile.

  /**
   * Hooks and state variables
   */
  const bookContentBasePath = useBookContentBasePath(); // gets base path for the book contents
  const appResourcePaths = useStaticResourcePaths(); // gets list of static resources for the app

  const [localSettings] = useState(getLocalSettingsProxy); // local settings

  const [initialAppHashMap, setInitialAppHashMap] = useState(); // map with downloaded app resources
  const [downloadedBookPaths, setDownloadedBookPaths] = useState(new Set()); // set of all downloaded book resources

  const [progress, setProgress] = useState(progressDefaults); // map with download progress for the book by markers, document and overall progress
  const [markersToDownload, setMarkersToDownload] = useState(new Set()); // set of markers to download
  const [markersDownloading, setMarkersDownloading] = useState(new Set()); // set of markers currently downloading
  const [markersDownloaded, setMarkersDownloaded] = useState(new Set()); // set of markers downloaded
  const [markersDownloadedIndex, setMarkersDownloadedIndex] = useState(
    new Set(),
  ); // set of markers indices downloaded
  const [documentsDownloaded, setDocumentsDownloaded] = useState(new Set()); // set of documents downloaded
  const [documentsDownloadedIndex, setDocumentsDownloadedIndex] = useState(
    new Set(),
  ); // set of documents indices downloaded

  const [wholeBookDownloaded, setWholeBookDownloaded] = useState(false); // boolean to flag if whole book is downloaded
  const [wholeBookDownloading, setWholeBookDownloading] = useState(false); // boolean to flag if whole book downloading

  const [nextCacheAction, setNextCacheAction] = useState(
    CACHE_CONTENT_ACTIONS.UPDATE_APP,
  );

  const offlineSessionBuffer = useRef(); // buffer to store offline session data before it is persisted to the backend
  const flushOfflineSessionBufferCallCount = useRef(0); // counter to track how many times flushOfflineSessionBuffer has been called
  const offlineSessionBufferSavePromise = useRef(); // promise to track the save of the offline session buffer

  /**
   * Download App to Cache
   */
  useEffect(function setHashMapForAppResources() {
    getCacheHashMap(CACHE_STORAGE_APP_CACHE_NAME).then(setInitialAppHashMap);
  }, []);

  /**
   * When the reader is first loaded, we populate the app cache with the resources needed to run the reader.
   * It goes into a non-book-specific cache so that it can be shared between books.
   * This happens before any book content is fetched.
   */
  const populateAppCache = useCallback(async () => {
    if (isMobileApp || nextCacheAction !== CACHE_CONTENT_ACTIONS.UPDATE_APP) {
      return;
    }

    if (!hasConnection || !initialAppHashMap || isSessionExpired) {
      // We cannot fetch resources if we do not have an internet connection.
      // We also need the initial hash map to know if any resources changed.
      return;
    }

    if (!appResourcePaths.length) {
      /**
       * When the provider loads for the first time, the resource list will be empty.
       * Because this useEffect clears the cache on each run,
       * we'll want to wait for the real resource list before executing the code below.
       */
      return;
    }

    // clear existing app cache
    await caches.delete(CACHE_STORAGE_APP_CACHE_NAME);

    if (!localSettings.useCacheStorage) {
      console.warn(
        'Warning: CacheStorage is disabled. ' +
          'This makes code changes apply faster, ' +
          'but it is not the same behavior as production environments and it breaks offline functionality.',
      );

      return;
    }

    const cache = await caches.open(CACHE_STORAGE_APP_CACHE_NAME);

    await cache.addAll(appResourcePaths);

    const updatedHashMap = await getCacheHashMap(CACHE_STORAGE_APP_CACHE_NAME);

    const changes = getCacheHashMapChanges(initialAppHashMap, updatedHashMap);

    if (changes.length) {
      console.log(
        'Clearing app cache and automatically reloading app. The following resources have changed:',
        changes,
      );

      /**
       * We stored the resources in the cache temporarily for the convenience of calling getCacheHashMapChanges
       * but we will wait until the next reload to store it with the intention of leaving it in the cache.
       *
       * We need to ensure that the version of the app we put in the cache is stable enough to boot up
       * and run the cache routine itself.
       *
       * This prevents broken states that prevent app loading/recaching from ending up in the cache,
       * which requires manual deletion of the cache to get the reader running again. AKA "bricked."
       * (in development, but this could theoretically happen in production as well)
       */
      await caches.delete(CACHE_STORAGE_APP_CACHE_NAME);

      window.location.reload();

      return;
    }

    setNextCacheAction(CACHE_CONTENT_ACTIONS.CLEAR_OUTDATED_BOOK_CONTENT);
  }, [
    hasConnection,
    initialAppHashMap,
    isMobileApp,
    isSessionExpired,
    localSettings,
    nextCacheAction,
    appResourcePaths,
  ]);

  useEffect(populateAppCache, [populateAppCache]);

  /**
   * Get absolute Paths from offlineSession persisted to backend
   * getAbsolutePaths calling multiple times can be avoided by storing the paths in state
   */
  const [persistedOfflineSession, setPersistedOfflineSession] = useState(
    offlineSessionDataDefaults,
  );

  useEffect(
    function getAbsolutePathsFromOfflineSession() {
      // offlineSession is undefined if current userContent was created without data for offlineSession.
      // else has a value or is null.

      offlineSessionDataReceived.current = Boolean(
        offlineSession !== undefined,
      );

      if (!offlineSessionDataReceived.current) {
        return;
      }

      if (!bookContentBasePath || !offlineSession) {
        setPersistedOfflineSession(offlineSessionDataDefaults);
        return;
      }

      if (offlineSession?.isPersisted) {
        const paths = offlineSession.getAbsolutePaths(bookContentBasePath);
        setPersistedOfflineSession({
          absolutePaths: paths?.absolutePaths,
          absolutePathsByDocument: paths?.absolutePathsByDocument,
          markers: new Set(offlineSession.markers),
          documents: new Set(offlineSession.documents),
        });
      }
    },
    [offlineSession, bookContentBasePath],
  );

  /**
   * Download Book To Cache
   */
  const lastDownloadStartTime = useRef(); // timestamp of when last attempt to add book resource was made
  const isCancelOngoing = useRef(false); // boolean to flag if cancel process started
  const documentAddedToCache = useRef(new Set()); // set of documents that is being added to cache
  const downloadedBookPathsRef = useRef(new Set()); // copy of downloadedBookPaths

  useEffect(
    function restartCacheActionsForChangedBookObject() {
      setNextCacheAction((nextCacheAction) => {
        /**
         * If the book changes, set the cache pipeline back to the start of the book cache process, or to whichever action it is executing, whichever is earlier.
         * This means it won't reset it forward past app caching stages, but will reset it back to the start of book caching.
         *
         * This is necessary because the first version of the book can be one pulled from cache itself,
         * so this lets it restart the caching process with the latest version of the book metadata.
         *
         * In practice, the reader will fetch updated book metadata faster than it can advance through the initial caching stages.
         * But this prevents a race condition and also handles any other edge case where the book metadata changes e.g. manual book refresh for search.
         */
        return Math.min(
          nextCacheAction,
          CACHE_CONTENT_ACTIONS.CLEAR_OUTDATED_BOOK_CONTENT,
        );
      });
    },
    [book],
  );

  useEffect(
    function clearOutdatedBookContent() {
      if (
        nextCacheAction !== CACHE_CONTENT_ACTIONS.CLEAR_OUTDATED_BOOK_CONTENT
      ) {
        return;
      }

      // Unlike other references to local settings in this provider that target global settings,
      // here we need a local settings proxy that is tied to the book pk.
      const localSettings = getLocalSettingsProxy(book.pk);

      // If the book has a new processing report pk, it will be different from what is in the local settings.
      // However, if the setting is not an integer, that means it has not been set at all.
      // In that case, we do not want to clear the cache. Instead we just set the value and move on.
      if (
        +localSettings.processingReportPk !== book.processingReportPk &&
        Number.isInteger(localSettings.processingReportPk)
      ) {
        console.warn(
          `Clearing book cache. The processing report id has changed from ${localSettings.processingReportPk} to ${book.processingReportPk}.`,
        );

        // Delete the entire cache for the book, which will force a re-download.
        caches.delete(`${CACHE_STORAGE_BOOK_CACHE_PREFIX}${book.pk}`);
      }

      // Update the processing report pk saved in local settings.
      localSettings.processingReportPk = book.processingReportPk;

      setNextCacheAction(CACHE_CONTENT_ACTIONS.UPDATE_BOOK_CONTENT);
    },
    [book, nextCacheAction],
  );

  const startedDownloadingAllDocuments = useRef();
  const cacheAllDocumentPathsInBook = useCallback(
    async (cache) => {
      // Ensure all documents are cached as long as either
      // a) the user is configured to always download all documents or
      // b) the user has an offline session with any documents (this should be any offline session with any content at all)
      if (
        !shouldAlwaysDownloadDocuments &&
        !persistedOfflineSession.documents.size
      ) {
        return;
      }

      // Avoid kicking off this method more than once.
      if (startedDownloadingAllDocuments.current) {
        return;
      }

      await cacheAllDocuments(book, cache);

      startedDownloadingAllDocuments.current = true;
    },
    [book, persistedOfflineSession.documents, shouldAlwaysDownloadDocuments],
  );

  /**
   * This method, which is called automatically, deletes cached book resources that are not included in the user's offline session.
   * It should be the first thing to check if you are developing offline-related functionality and your local cache is clearing itself on every refresh.
   *
   * @method deleteDisincludedBookResources
   * @param {Cache} cache - The book-specific CacheStorage object to query.
   */
  const deleteDisincludedBookResources = useCallback(
    async (cache) => {
      const downloadedPaths = await getDownloadedPathsFromCache(cache);
      const pathsRemainingInCache = new Set(); // paths still in offline Session

      for (let absolutePath of downloadedPaths) {
        // Document HTML should be kept regardless of the user's offline session.
        if (absolutePath.startsWith('/spine/')) {
          continue;
        }

        if (!persistedOfflineSession.absolutePaths?.has(absolutePath)) {
          await cache.delete(absolutePath);
          continue;
        }

        pathsRemainingInCache.add(absolutePath);
      }

      downloadedBookPathsRef.current = pathsRemainingInCache;
      setDownloadedBookPaths(downloadedBookPathsRef.current);
    },
    [persistedOfflineSession],
  );

  const checkIsSessionExpired = useCallback(() => {
    // fetch a path to see if session (not offlineSession, total user sessions) is expired or not.
    // this exists only coz cache.add() does not provide network errors
    // cache.put() gives back network responses but it messes downloading.

    if (
      !persistedOfflineSession.absolutePaths.size || // No offline session created by user
      isOffline // Not online
    ) {
      return;
    }

    const [path] = persistedOfflineSession.absolutePaths;

    if (path) {
      fetch(path)
        .then((res) => {
          if (res.status === STATUS_CODES.FORBIDDEN) {
            setIsSessionExpired(true);
            return true;
          }
        })
        .catch((err) => {
          console.warn(err);
        });
    }
  }, [isOffline, persistedOfflineSession.absolutePaths, setIsSessionExpired]);

  /**
   * Async function to add Book Resources to Cache
   * All book content specified by the user's offline session will be automatically downloaded to the cache.
   * This excludes only document HTML files, which are handled separately -- see updateDocumentContent.
   */
  useEffect(() => {
    const populateBookCache = async () => {
      const downloadStartedTime = new Date().getTime();
      lastDownloadStartTime.current = downloadStartedTime;

      // No Book or offlineSession data is not received yet, exit
      if (!book || !offlineSessionDataReceived.current) {
        return;
      }

      // if session expired, fetching to add to cache will result in 403, so exit
      if (isSessionExpired || checkIsSessionExpired()) {
        return;
      }

      // For mobile- set downloadedBookPaths to all paths that needed to be added to cache for offline.
      // Request for these paths will be spoofed in mobileApp to give offline experience in mobile devices.
      if (isMobileApp) {
        setDownloadedBookPaths(persistedOfflineSession.absolutePaths);
        return;
      }

      const cache = await caches.open(
        `${CACHE_STORAGE_BOOK_CACHE_PREFIX}${book.pk}`,
      );

      await deleteDisincludedBookResources(cache);

      // Not ready to download book resources to cache yet,
      // either because the app is offline or it has not yet populated the app cache.
      if (
        isOfflineForReader || // Not online
        nextCacheAction !== CACHE_CONTENT_ACTIONS.UPDATE_BOOK_CONTENT // Wait until app cache is populated
      ) {
        return;
      }

      await cacheAllDocumentPathsInBook(cache);

      // If there's no actual offline session, go no further.
      if (!persistedOfflineSession.absolutePaths.size) {
        return;
      }

      /**
       * Iterate through all documents in offline session and
       * Add the resource path for each document to cache
       * while checking for duplicates, cancelations and redownloads.
       * Keep track of downloaded path and document to calculate download progress.
       */
      for (let document of persistedOfflineSession.documents) {
        documentAddedToCache.current.add(document);
        const absolutePaths =
          persistedOfflineSession.absolutePathsByDocument?.get(document) ?? [];

        for (let absolutePath of absolutePaths) {
          if (
            downloadStartedTime !== lastDownloadStartTime.current || // Stop downloading if a new download has started.
            isCancelOngoing.current // or if cancel download is in progress
          ) {
            return;
          }
          // Move to next if already downloaded this path
          if (downloadedBookPathsRef.current.has(absolutePath)) {
            continue;
          }

          try {
            await cache.add(absolutePath);

            // Update the list of downloaded paths and currently downloading documents
            setDownloadedBookPaths((prev) => {
              downloadedBookPathsRef.current = new Set(prev.add(absolutePath));
              return downloadedBookPathsRef.current;
            });
          } catch (error) {
            console.warn(error);
          }
        }
      }
    };

    populateBookCache();
  }, [
    book,
    checkIsSessionExpired,
    deleteDisincludedBookResources,
    isMobileApp,
    isOfflineForReader,
    nextCacheAction,
    persistedOfflineSession,
    isSessionExpired,
    cacheAllDocumentPathsInBook,
  ]);

  /**
   * Download Progress Calculations
   */
  const getProgress = useCallback(() => {
    // In mobile, offline session is available for whole book so all documents are guaranteed to be available offline.
    const isBookDownloadedForMobileApp =
      isMobileApp && Boolean(persistedOfflineSession.absolutePaths.size);
    const documentAddedToCacheSet = documentAddedToCache.current;

    // Calculate and Update progress for downloaded paths and documents
    setProgress((progress) => {
      const documentsProgress = getDocumentsProgress(
        progress.documentsProgress,
        persistedOfflineSession,
        downloadedBookPaths,
        isBookDownloadedForMobileApp,
      );

      const allDocumentsCompletedDownloading = Array.from(
        documentsProgress.values(),
      ).every((document) => document.isComplete);

      const overallProgress = getOverallProgress(
        downloadedBookPaths,
        persistedOfflineSession.absolutePaths,
        allDocumentsCompletedDownloading,
      );

      const markersProgress = getMarkersProgress(
        persistedOfflineSession.markers,
        documentsProgress,
        progress.markersProgress,
        documentAddedToCacheSet,
      );

      return { overallProgress, documentsProgress, markersProgress };
    });

    if (documentAddedToCache.current.size) {
      documentAddedToCache.current = new Set();
    }
  }, [
    documentAddedToCache,
    downloadedBookPaths,
    isMobileApp,
    persistedOfflineSession,
  ]);

  const lastProgressUpdate = useRef(0); // time when last progress was calculated
  useEffect(
    function calculateProgress() {
      // If no paths downloaded, no progress
      if (!downloadedBookPaths?.size) {
        setProgress({
          overallProgress: new Map(),
          documentsProgress: new Map(),
          markersProgress: new Map(),
        });
        return;
      }

      // No need to recalculate if last calculation was less than 5 seconds ago and not all paths have been downloaded
      const now = new Date().getTime();
      const allPathsDownloaded =
        persistedOfflineSession.absolutePaths.size &&
        downloadedBookPaths.size === persistedOfflineSession.absolutePaths.size;

      if (allPathsDownloaded || now - lastProgressUpdate.current > 5000) {
        getProgress();
        lastProgressUpdate.current = now;
      }
    },
    [downloadedBookPaths, getProgress, persistedOfflineSession],
  );

  /**
   * Update offline status pointers from progress
   */
  useEffect(
    function updateOfflineStatusPointers() {
      //updateDocumentsDownloaded
      let hasDocumentsDownloadedChanged = false;
      const newDocumentsDownloaded = getMatchingMapEntryKeys(
        progress.documentsProgress,
        (v) => v.isComplete,
      );
      setDocumentsDownloaded((prevDocuments) => {
        const noNewDocumentsDownloaded = areSetsEqual(
          prevDocuments,
          newDocumentsDownloaded,
        );

        // Skip if no changes
        if (noNewDocumentsDownloaded) {
          return prevDocuments;
        }

        hasDocumentsDownloadedChanged = true;
        return newDocumentsDownloaded;
      });
      // track indices of documentsDownloaded
      setDocumentsDownloadedIndex((prev) =>
        hasDocumentsDownloadedChanged
          ? getIndices(newDocumentsDownloaded)
          : prev,
      );

      // updateMarkersDownloading
      const newMarkersDownloading = getMatchingMapEntryKeys(
        progress.markersProgress,
        (v) => !v.isComplete && Boolean(v.percentage),
      );
      setMarkersDownloading((prevMarkersDownloading) => {
        const noNewMarkersDownloading = areSetsEqual(
          prevMarkersDownloading,
          newMarkersDownloading,
        );

        // Skip if no changes
        if (noNewMarkersDownloading) {
          return prevMarkersDownloading;
        }

        return newMarkersDownloading;
      });

      // updateMarkersDownloaded
      const newMarkersDownloaded = getMatchingMapEntryKeys(
        progress.markersProgress,
        (v) => v.isComplete,
      );

      // Calculate hasMarkersDownloadedChanged BEFORE setState calls
      const hasMarkersDownloadedChanged = !areSetsEqual(
        markersDownloaded,
        newMarkersDownloaded,
      );

      if (hasMarkersDownloadedChanged) {
        setMarkersDownloaded(newMarkersDownloaded);
      }
      // track indices of markersDownloaded
      setMarkersDownloadedIndex((prev) =>
        hasMarkersDownloadedChanged ? getIndices(newMarkersDownloaded) : prev,
      );

      // updateMarkersToDownload
      if (hasMarkersDownloadedChanged) {
        setMarkersToDownload((prev) => {
          const newMarkersToDownload = new Set();

          prev?.forEach((m) => {
            if (newMarkersDownloaded.has(m)) {
              return;
            }

            newMarkersToDownload.add(m);
          });

          return newMarkersToDownload;
        });
      }
    },
    [progress, markersDownloaded],
  );

  /**
   * Update flags for complete book downloads
   */
  useEffect(
    function checkIsWholeBookDownloaded() {
      // For TOC books, markersDownloaded includes all topLevelMarkers, or
      // For noTOC books, documentsDownloaded includes all book documents

      const wholeBookDownloaded = book.topLevelMarkers?.length
        ? Boolean(book.topLevelMarkers.every((m) => markersDownloaded.has(m)))
        : Boolean(book.documents.every((d) => documentsDownloaded.has(d)));

      setWholeBookDownloaded(wholeBookDownloaded);
    },
    [book, documentsDownloaded, markersDownloaded],
  );

  useEffect(
    function checkIsWholeBookDownloading() {
      // For TOC books, offlineSession markers includes all topLevelMarkers (checking topLevelMarkers is enough as toplevelMarker can't be downloaded without downloading all its children)
      // For noTOC books, wholeBook flag is true.

      if (
        !offlineSession || // No offline Session
        !offlineSession?.isPersisted || // offline Session updated but not persisted
        wholeBookDownloaded // whole book download complete
      ) {
        // whole book is not downloading anymore.

        setWholeBookDownloading(false);
        return;
      }

      const wholeBookDownloading = book.topLevelMarkers?.length
        ? Boolean(
            book.topLevelMarkers.every((m) =>
              offlineSession.markers.includes(m),
            ),
          )
        : Boolean(offlineSession?.wholeBook);

      setWholeBookDownloading(wholeBookDownloading);
    },
    [book, offlineSession, wholeBookDownloaded],
  );

  const checkIsChapterDownloaded = useCallback(
    (marker) => {
      const isNoTOCBook = !Boolean(book.topLevelMarkers?.length);

      if (!marker || isNoTOCBook) {
        return wholeBookDownloaded;
      }

      return Boolean(markersDownloadedIndex?.has(marker?.index));
    },
    [book, markersDownloadedIndex, wholeBookDownloaded],
  );

  /**
   * ContentState for OfflineMenu
   */
  const [offlineMenuProps, setOfflineMenuProps] = useState({
    markerStatusMap: new Map(), // Map of offline marker to its status
    markersForDownloadedContentSection: new Set(), // Set of topLevelMarkers that have downloaded content
    markersShouldOpen: new Set(), // set of markers which should have expanded accordion by default
  });

  useEffect(
    function createMarkerStatusMap() {
      const newMarkerStatusMap = new Map();
      const newMarkersForDownloadedContentSection = new Set();
      const newMarkersShouldOpen = new Set();

      markersDownloading?.forEach((markerDownloading) =>
        // For markers in markersDownloading, set status as DOWNLOADING
        newMarkerStatusMap.set(
          markerDownloading,
          OFFLINE_CHAPTER_STATUS.DOWNLOADING,
        ),
      );

      markersDownloaded?.forEach((markerDownloaded) => {
        // For markers in markersDownloaded, set status as DOWNLOADED
        newMarkerStatusMap.set(
          markerDownloaded,
          OFFLINE_CHAPTER_STATUS.DOWNLOADED,
        );

        // Any topLevelMarker that has downloaded content should be in 'Downloaded Content' section of offlineMenu
        newMarkersForDownloadedContentSection.add(
          markerDownloaded.topLevelMarker,
        );

        //Anything that doesn't have downloaded content, don't need default expanded accordions
        // If topLevelMarker is downloaded, downloading or to-download, dont expand
        if (
          markersDownloaded?.has(markerDownloaded.topLevelMarker) ||
          markersDownloading?.has(markerDownloaded.topLevelMarker) ||
          markersToDownload?.has(markerDownloaded.topLevelMarker) ||
          wholeBookDownloading
        ) {
          return;
        }

        // If topLevelMarker is not downloaded, that should open
        newMarkersShouldOpen.add(markerDownloaded.topLevelMarker);

        // Going up the parentMarker until it reaches the topLevelMarker
        // to see if any of the parentMarker should be open or closed
        let parentMarker = markerDownloaded.parent;

        // If parentMarker is downloaded, downloading or to-download, dont expand
        // coz all the childMarkers will eventually be same state as parentMarker eventually
        while (!parentMarker.isTopLevelMarker) {
          if (
            markersDownloaded?.has(parentMarker) ||
            markersDownloading?.has(parentMarker) ||
            markersToDownload?.has(parentMarker) ||
            wholeBookDownloading
          ) {
            break;
          }

          newMarkersShouldOpen.add(parentMarker);
          parentMarker = parentMarker.parent;
        }
      });

      markersToDownload?.forEach((markerToDownload) => {
        // For marker in markerToDownload, itself and all its children should be in QUEUE unless children has other status
        markerToDownload.withDescendents.forEach(
          (marker) =>
            newMarkerStatusMap.get(marker) ??
            newMarkerStatusMap.set(marker, OFFLINE_CHAPTER_STATUS.QUEUE),
        );
      });

      const newOfflineMenuProps = {
        markerStatusMap: newMarkerStatusMap,
        markersForDownloadedContentSection:
          newMarkersForDownloadedContentSection,
        markersShouldOpen: newMarkersShouldOpen,
      };

      setOfflineMenuProps(newOfflineMenuProps);
    },
    [
      markersDownloaded,
      markersDownloading,
      markersToDownload,
      wholeBookDownloading,
    ],
  );

  /**
   * Flush Offline Session Buffer
   * This function is called to save the offline session buffer to the backend.
   * It ensures that the book is ready for offline before saving and handles the case where multiple calls to this function are made.
   * It also ensures that the offline session buffer is saved only once for the most recent call to this function.
   *
   * @method flushOfflineSessionBuffer
   * @returns {Promise} - A promise that resolves when the offline session buffer is saved.
   */
  const flushOfflineSessionBuffer = useCallback(async () => {
    const currentFlushNumber = flushOfflineSessionBufferCallCount.current + 1;
    flushOfflineSessionBufferCallCount.current = currentFlushNumber;

    // Ensure the book is ready for offline before saving.
    if (!promiseBookReadyForOfflineRef.current) {
      promiseBookReadyForOfflineRef.current = promiseBookReadyForOffline(
        client,
        book.pk,
      );
    }
    await promiseBookReadyForOfflineRef.current;

    // Ensure there is still something to flush.
    if (!offlineSessionBuffer.current) {
      return;
    }

    // Only call the flush function for the most recent call to this function.
    if (currentFlushNumber === flushOfflineSessionBufferCallCount.current) {
      // If the offline session buffer is has no markers, delete it.
      if (
        !offlineSessionBuffer.current?.markerIndices?.length &&
        !offlineSessionBuffer.current?.wholeBook
      ) {
        offlineSessionBufferSavePromise.current =
          offlineSessionBuffer.current.delete();
        offlineSessionBuffer.current = null;
      } else {
        // Save the offline session buffer to the backend.
        offlineSessionBufferSavePromise.current =
          offlineSessionBuffer.current.save();
      }
    }

    const result = await offlineSessionBufferSavePromise.current;

    // After saving, refetch user content to get the session with documentResources
    if (result && userContent?.refetch) {
      await userContent.refetch(['offlineSessions']);
    }

    return result;
  }, [book.pk, client, userContent]);

  /**
   * The offline session buffer will usually be more up-to-date than the stateful offline session object,
   * except when loading for the first time.
   *
   * Simply placing the offline session buffer in the state with useState(offlineSession) will not work,
   * because the initial state will be null until user content is loaded and it will not re-instatiate the useState hook.
   *
   * Instead, this function will update the offline session buffer whenever the offline session changes and is persisted.
   * This handles both the initial load case and any other possible case where the provided offlineSession value changes.
   */
  useEffect(
    function updateOfflineSessionBuffer() {
      if (offlineSession?.isPersisted) {
        offlineSessionBuffer.current = offlineSession;
      }
    },
    [offlineSession],
  );

  /**
   * Adding and Removing Markers from download
   */
  const currentMarkersInSession = useRef([]); // all markers the user has clicked added now/in previous sessions

  const addMarkers = useCallback(
    async (markers, wholeBook = false) => {
      const existingMarkers = offlineSessionBuffer.current?.markers ?? [];

      const markersToAdd = markers
        .map((marker) => marker.withDescendents)
        .flat();

      const markerIndices = [
        ...new Set([...existingMarkers, ...markersToAdd]),
      ].map((marker) => marker.index);

      setMarkersToDownload((prev) => {
        if (!prev) {
          return new Set(markers);
        }

        const newMarkersToDownload = new Set([...prev]);

        markers.forEach((m) => newMarkersToDownload.add(m));
        return newMarkersToDownload;
      });

      // all unique markers from this / past sessions
      // we need this because sometimes we loose marker indexes when the user adds them fast
      // as the frontend doesn't have the first request response before sending over the next one
      currentMarkersInSession.current = [
        ...new Set([...currentMarkersInSession.current, ...markerIndices]),
      ];

      if (offlineSessionBuffer.current) {
        // Add to existing offline session
        offlineSessionBuffer.current = offlineSessionBuffer.current.change({
          markerIndices: currentMarkersInSession.current,
          wholeBook,
        });
        return await flushOfflineSessionBuffer();
      }

      // Create a new offline session
      offlineSessionBuffer.current = new UserContentOfflineSession({
        book,
        deviceInfo: { uuid: deviceUUID },
        markerIndices,
        wholeBook,
      });

      return await flushOfflineSessionBuffer();
    },
    [book, deviceUUID, flushOfflineSessionBuffer],
  );

  const removeMarkers = useCallback(
    async (markers, removeChildren = true) => {
      if (!offlineSessionBuffer.current) {
        // No session, nothing to remove

        return;
      }

      const markersToRemove = removeChildren
        ? [
            ...new Set(
              [
                ...markers.map((marker) => marker.withParents),
                ...markers.map((marker) => marker.withDescendents),
              ].flat(),
            ),
          ]
        : markers;

      const existingMarkers = offlineSessionBuffer.current?.markers ?? [];
      const markerIndices = existingMarkers
        .filter((marker) => !markersToRemove.includes(marker))
        .map((marker) => marker.index);

      const markersToRemoveIndexes = markersToRemove.map(
        (marker) => marker.index,
      );

      // filter out any removed markers from the markers we're adding to the offline session
      currentMarkersInSession.current = currentMarkersInSession.current.filter(
        (currentMarker) => !markersToRemoveIndexes.includes(currentMarker),
      );

      setMarkersToDownload((prev) => {
        const newMarkersToDownload = new Set();

        prev?.forEach((m) => {
          if (markers.includes(m)) {
            return;
          }

          newMarkersToDownload.add(m);
        });

        return newMarkersToDownload;
      });

      // Update offlineSession with remaining markerIndices
      offlineSessionBuffer.current = offlineSessionBuffer.current.change({
        markerIndices,
        wholeBook: false,
      });

      return await flushOfflineSessionBuffer();
    },
    [flushOfflineSessionBuffer],
  );

  const addMarker = useCallback(
    // Syntactic sugar for calling addMarkers on a single marker

    async (marker) => await addMarkers([marker]),
    [addMarkers],
  );

  const removeMarker = useCallback(
    async (marker, removeChildren = true) =>
      // Syntactic sugar for calling removeMarkers on a single marker

      await removeMarkers([marker], removeChildren),
    [removeMarkers],
  );

  const downloadWholeBook = useCallback(async () => {
    // For TOC books, add all topLevelMarkers to session
    // For noTOC books, make offline session with wholeBook flag to true

    Boolean(book.topLevelMarkers.length)
      ? addMarkers(book.topLevelMarkers)
      : await addMarkers([], true);
  }, [addMarkers, book.topLevelMarkers]);

  /**
   * Cancel and Clear Downloading
   */
  const clear = useCallback(async () => {
    // This will delete whole offline session

    isCancelOngoing.current = true;

    setMarkersToDownload(new Set());
    currentMarkersInSession.current = [];

    if (offlineSessionBuffer.current) {
      offlineSessionBuffer.current = offlineSessionBuffer.current.change({
        markerIndices: [],
        wholeBook: false,
      });
    }
    const flush = await flushOfflineSessionBuffer();

    isCancelOngoing.current = false;
    return flush;
  }, [flushOfflineSessionBuffer]);

  const cancel = useCallback(
    async (markers) => {
      // This will delete files for partially downloaded markers and Stop downloading any marker that isn't already downloaded

      if (markers) {
        // If cancel called with markers, remove the markers if not already downloaded.

        markers = [...[markers]].flat();

        const markersToCancel = [
          ...new Set(
            [
              ...markers.map((marker) => marker.withParents),
              ...markers.map((marker) => marker.withDescendents),
            ].flat(),
          ),
        ];

        const markersToCancelNotAlreadyDownloaded = markersToCancel.filter(
          (marker) => !markersDownloaded.has(marker),
        );

        await removeMarkers(markersToCancelNotAlreadyDownloaded, false);
      } else {
        // If cancel called without markers
        // If no markers have been downloaded - clear offline session
        // If there are markers downloaded- remove all except downloaded markers

        isCancelOngoing.current = true;

        if (markersDownloaded.size) {
          await removeMarkers(
            offlineSessionBuffer.current?.markers?.filter(
              (marker) => !markersDownloaded.has(marker),
            ) ?? [],
            false,
          );
        } else {
          await clear();
        }

        isCancelOngoing.current = false;
      }
    },
    [clear, markersDownloaded, removeMarkers],
  );

  /**
   * Error Dialog when Downloading Interrupted
   */
  const [downloadInterruptionError, setDownloadInterruptionError] =
    useState(null); // flag to show error dialog on dowloading interruption
  const [offlineErrorSetOnce, setOfflineErrorSetOnce] = useState(false); // boolean to flag if offlineError has been set

  const clearAddMarkerToOfflineError = useCallback(() => {
    // Remove offline Error Dialog

    setDownloadInterruptionError(null);
  }, []);

  useEffect(
    function checkShouldOfflineErrorDialogShow() {
      if (!isOfflineForReader) {
        // Remove error if online

        setDownloadInterruptionError(null);
        setOfflineErrorSetOnce(false);
      }

      if (
        isOfflineForReader && // app is offline
        Boolean(markersDownloading.size) && // download is in progress
        !offlineErrorSetOnce // offline error has not set before
      ) {
        // If network interrupted in middle of downloading and is first time showing error, show error message

        setDownloadInterruptionError(true);
        setOfflineErrorSetOnce(true);
        return;
      }
    },
    [markersDownloading, isOfflineForReader, offlineErrorSetOnce],
  );

  /**
   * 'Access Your Content Offline' Information Dialog
   */
  const [
    isAccessOfflineContentDialogOpen,
    setIsAccessOfflineContentDialogOpen,
  ] = useState(false); // boolean to flag if the Dialog is to open

  const openAccessOfflineContentDialog = useCallback(() => {
    if (!localSettings.accessOfflineContentDialog.shouldDisplay) {
      return;
    }

    const hasCloseTimeoutExpired =
      +new Date() - localSettings.accessOfflineContentDialog.closeTimestamp >
      ACCESS_OFFLINE_CONTENT_DIALOG_TIMEOUT;

    if (hasCloseTimeoutExpired) {
      setIsAccessOfflineContentDialogOpen(true);
    }
  }, [
    localSettings.accessOfflineContentDialog.shouldDisplay,
    localSettings.accessOfflineContentDialog.closeTimestamp,
    setIsAccessOfflineContentDialogOpen,
  ]);

  const closeAccessOfflineContentDialog = useCallback(
    (dontShowAgain) => {
      setIsAccessOfflineContentDialogOpen(false);
      localSettings.accessOfflineContentDialog.closeTimestamp = +new Date();
      localSettings.accessOfflineContentDialog.shouldDisplay = !dontShowAgain;
    },
    [
      localSettings.accessOfflineContentDialog,
      setIsAccessOfflineContentDialogOpen,
    ],
  );

  const values = useMemo(
    () => ({
      /**
       * Check if a marker/book is downloaded
       */
      checkIsChapterDownloaded,
      progress: progress.overallProgress, // map with overall progress information
      /**
       * The offline UI communicates content availability using markers.
       * These properties will be the most useful for that purpose.
       */
      markersProgress: progress.markersProgress, // Map of progress for markers
      markersDownloading, // set of markers downloading
      markersDownloaded, // set of markers downloaded
      markersDownloadedIndex, // set of indices of markersDownloaded
      markersToDownload, // set of markers to download
      /**
       * We only need to consider which downloads are available
       * for the sake of displaying either the Document component or a placeholder.
       */
      documentsDownloaded, // set of documents downloaded for offline
      documentsDownloadedIndex, // set of indices of documentsDownloaded
      wholeBookDownloaded, // boolean to flag if whole book has been downloaded for offline
      wholeBookDownloading, // boolean to flag if whole book is downloading
      isAccessOfflineContentDialogOpen, // boolean to open 'Access Your Content Offline' Information Dialog
      offlineMenuProps,
    }),
    [
      checkIsChapterDownloaded,
      progress,
      markersDownloading,
      markersDownloaded,
      documentsDownloaded,
      documentsDownloadedIndex,
      markersToDownload,
      markersDownloadedIndex,
      wholeBookDownloaded,
      wholeBookDownloading,
      isAccessOfflineContentDialogOpen,
      offlineMenuProps,
    ],
  );

  const modifiers = useMemo(
    () => ({
      /**
       * Add or remove markers from the offline session.
       * Creating the offline session itself is handled automatically.
       */
      addMarker,
      addMarkers,
      removeMarker,
      removeMarkers,
      downloadWholeBook,
      /**
       * Cancel in-progress downloads.
       * Any marker not already downloaded will be discarded from the session.
       */
      cancel,
      /**
       * Remove all markers, ending the session.
       */
      clear,
      /***
       * Error and Information Dialogs
       */
      downloadInterruptionError,
      clearAddMarkerToOfflineError,
      openAccessOfflineContentDialog,
      closeAccessOfflineContentDialog,
    }),
    [
      downloadWholeBook,
      addMarker,
      addMarkers,
      removeMarker,
      removeMarkers,
      cancel,
      clear,
      downloadInterruptionError,
      clearAddMarkerToOfflineError,
      openAccessOfflineContentDialog,
      closeAccessOfflineContentDialog,
    ],
  );

  /**
   * Special useEffect just for debugging purposes.
   *
   * Devtools API with flexible input:
   * v7.offline.add(bookMarker, bookMarker)
   * v7.offline.add([bookMarker, bookMarker])
   * v7.offline.add([100, bookMarker])
   * v7.offline.add(23, 8)
   * v7.offline.remove(bookMarker, 2)
   */
  useEffect(
    function debugHelper() {
      const mutate = (...x) =>
        (Array.isArray(x[0]) ? x[0] : x).map((x) =>
          Number.isInteger(x) ? book.markers[x] : x,
        );
      window.v7.offline = {
        ...values,
        ...modifiers,
        session: offlineSession,
        add: async (...x) => {
          await addMarkers(mutate(...x));
        },
        remove: async (...x) => {
          await removeMarkers(mutate(...x));
        },
        offlineErrorSetOnce,
      };
    },
    [
      addMarkers,
      book,
      modifiers,
      offlineSession,
      offlineErrorSetOnce,
      removeMarkers,
      values,
    ],
  );

  return (
    <OfflineValuesContext.Provider value={values}>
      <OfflineModifiersContext.Provider value={modifiers}>
        {children}
      </OfflineModifiersContext.Provider>
    </OfflineValuesContext.Provider>
  );
};

export default OfflineProvider;
